import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import BookingBanner from '../components/BookingBanner'
import { getAssetUrl } from '../lib/utils'

export default function TreatmentDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [treatment, setTreatment] = useState(null)
  const [allTreatments, setAllTreatments] = useState([])
  const [relatedTestimonials, setRelatedTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        const [treatRes, testRes] = await Promise.all([
          fetch('/_api/public/content/treatments'),
          fetch('/_api/public/content/testimonials'),
        ])

        if (treatRes.ok) {
          const treatData = await treatRes.json()
          const items = Array.isArray(treatData) ? treatData : treatData.items || []
          setAllTreatments(items)
          const found = items.find((t) => {
            const itemSlug = typeof t.slug === 'object' ? t.slug?._ : t.slug
            return itemSlug === slug
          })
          setTreatment(found || null)
        }

        if (testRes.ok) {
          const testData = await testRes.json()
          const testItems = Array.isArray(testData) ? testData : testData.items || []
          setRelatedTestimonials(testItems)
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af behandling:', err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [slug])

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 text-center text-sm text-[#556B48] animate-pulse">
        Indlæser behandling...
      </div>
    )
  }

  if (!treatment) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center space-y-4">
        <h1 className="font-serif text-3xl font-normal text-[#2B3824]">
          Behandlingen blev ikke fundet
        </h1>
        <p className="text-sm text-[#6A5464]">
          Vi kunne desværre ikke finde den ønskede behandling.
        </p>
        <Link
          to="/behandlinger"
          className="inline-flex items-center gap-2 rounded-full bg-[#5C7A4F] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#455C3A]"
        >
          <span>Gå til behandlingsoversigt</span>
          <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
        </Link>
      </div>
    )
  }

  const imgUrl = Array.isArray(treatment.hero_image)
    ? treatment.hero_image[0]
    : treatment.hero_image

  // Find related treatments (excluding current)
  const relatedTreatments = allTreatments
    .filter((t) => {
      const itemSlug = typeof t.slug === 'object' ? t.slug?._ : t.slug
      return itemSlug !== slug
    })
    .slice(0, 3)

  // Find matching testimonials for this treatment
  const treatmentTitleLower = treatment.title.toLowerCase()
  const matchingTestimonials = relatedTestimonials.filter((t) => {
    const catLower = (t.category || '').toLowerCase()
    return (
      catLower.includes(slug) ||
      treatmentTitleLower.includes(catLower) ||
      catLower.includes('angst') && slug === 'angst' ||
      catLower.includes('rygestop') && slug === 'rygestop' ||
      catLower.includes('vægttab') && slug === 'vaegttab'
    )
  }).slice(0, 2)

  return (
    <>
      <MetaTags
        title={treatment.meta_title || `${treatment.title} - Trine Rosendal`}
        description={treatment.meta_description || treatment.short_description}
        image={imgUrl}
        canonicalUrl={`https://trine-rosendal.dk/behandlinger/${slug}`}
      />

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-14 space-y-12 sm:space-y-16">
        {/* Breadcrumb & Back Navigation */}
        <nav aria-label="Brødkrummer" className="flex items-center gap-2 text-xs text-[#5C6E52]">
          <Link to="/" className="hover:text-[#222E1D] transition">
            Forside
          </Link>
          <span>/</span>
          <Link to="/behandlinger" className="hover:text-[#222E1D] transition">
            Behandlinger
          </Link>
          <span>/</span>
          <span className="font-medium text-[#222E1D] truncate max-w-[200px] sm:max-w-none">
            {treatment.title}
          </span>
        </nav>

        {/* Title Header */}
        <div className="space-y-4 max-w-3xl">
          {treatment.category_name && (
            <span className="inline-flex items-center rounded-full bg-[#E2EBD9] px-3.5 py-1 text-xs font-medium text-[#5C7A4F]">
              {treatment.category_name}
            </span>
          )}

          <h1 className="font-serif text-3.5xl sm:text-4xl lg:text-5xl font-medium text-[#222E1D] leading-tight">
            {treatment.title}
          </h1>

          {treatment.short_description && (
            <p className="text-base sm:text-lg text-[#4D5E46] leading-relaxed">
              {treatment.short_description}
            </p>
          )}

          <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
            <a
              href="https://system.easypractice.net/book/online-booking-16"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#F5D154] hover:bg-[#EABF35] px-6 py-3 text-xs sm:text-sm font-semibold text-[#1E2718] shadow-sm transition"
            >
              <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
              <span>Book tid til denne behandling</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#D6E2CB] bg-[#F7F9F3] px-5 py-3 text-xs sm:text-sm font-medium text-[#222E1D] transition hover:bg-[#EAF1E4]"
            >
              <i className="fa-solid fa-envelope text-xs text-[#556B48]" aria-hidden="true" />
              <span>Stil et spørgsmål</span>
            </Link>
          </div>
        </div>

        {/* Hero Image */}
        {imgUrl && (
          <div className="overflow-hidden rounded-3xl border border-[#DFE6D5] bg-[#F6F8F3] shadow-sm">
            <img
              src={getAssetUrl(imgUrl, { width: 1200, quality: 85 })}
              alt={treatment.title}
              className="h-[320px] sm:h-[440px] w-full object-cover"
              onError={(e) => {
                e.currentTarget.src = imgUrl
              }}
            />
          </div>
        )}

        {/* Main Content Area (Verbatim HTML from scrape) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="rounded-3xl border border-[#DFE6D5] bg-[#F6F8F3] p-7 sm:p-10 shadow-xs">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: treatment.content }}
              />
            </div>

            {/* Testimonials related to this treatment if available */}
            {matchingTestimonials.length > 0 && (
              <div className="mt-10 space-y-4">
                <h3 className="font-serif text-2xl font-normal text-[#242E1F]">
                  Hvad klienter fortæller om lignende forløb
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {matchingTestimonials.map((t, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-[#DFE6D5] bg-[#FDFBFD] p-5 shadow-xs space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-serif font-medium text-[#2B3824]">
                          {t.name}
                        </span>
                        <span className="text-[11px] rounded-full bg-[#E7EEDF] px-2.5 py-0.5 text-[#5C7A4F]">
                          {t.category}
                        </span>
                      </div>
                      <div
                        className="text-xs text-[#523D4D] leading-relaxed line-clamp-5"
                        dangerouslySetInnerHTML={{ __html: t.quote }}
                      />
                      <Link
                        to="/referencer"
                        className="inline-block text-[11px] font-medium text-[#5C7A4F] underline"
                      >
                        Læs alle referencer →
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Practical info & Booking Box */}
          <div className="lg:col-span-4 space-y-6">
            {/* Practical info card */}
            <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 space-y-4">
              <h3 className="font-serif text-lg font-medium text-[#222E1D]">
                Praktisk information
              </h3>

              <div className="space-y-3 text-xs text-[#4D5E46]">
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-clock text-[#556B48] mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="font-semibold text-[#222E1D] block">Varighed</span>
                    <span>1 time og 20 minutter pr. behandling</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-tag text-[#556B48] mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="font-semibold text-[#222E1D] block">Pris</span>
                    <span>1.400,- DKK (Søndag 1.600,- DKK)</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-envelope text-[#556B48] mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="font-semibold text-[#222E1D] block">Opfølgning</span>
                    <span>Fri mailvejledning mellem behandlingerne</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-credit-card text-[#556B48] mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="font-semibold text-[#222E1D] block">Betaling</span>
                    <span>MobilePay eller kontant</span>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-[#D6E2CB]">
                <a
                  href="https://system.easypractice.net/book/online-booking-16"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5C7A4F] px-4 py-2.5 text-xs font-medium text-white shadow-xs transition hover:bg-[#455C3A]"
                >
                  <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
                  <span>Book tid online her</span>
                </a>
              </div>
            </div>

            {/* Direct Contact Card */}
            <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 space-y-3">
              <h4 className="font-serif text-base font-medium text-[#222E1D]">
                Har du spørgsmål?
              </h4>
              <p className="text-xs text-[#5C6E52] leading-relaxed">
                Du er altid velkommen til at kontakte mig for at høre mere om, hvordan et forløb kan tilrettelægges.
              </p>
              <div className="space-y-1.5 pt-1 text-xs">
                <a
                  href="tel:42510105"
                  className="flex items-center gap-2 font-medium text-[#222E1D] hover:text-[#556B48] whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[#556B48]" aria-hidden="true" />
                  <span className="whitespace-nowrap">Tlf: 42 51 01 05</span>
                </a>
                <a
                  href="mailto:info@trine-rosendal.dk"
                  className="flex items-center gap-2 font-medium text-[#222E1D] hover:text-[#556B48] whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-[#556B48]" aria-hidden="true" />
                  <span className="whitespace-nowrap">info@trine-rosendal.dk</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related Treatments */}
        {relatedTreatments.length > 0 && (
          <div className="border-t border-[#DFE6D5] pt-12 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl font-medium text-[#242E1F]">
                Andre behandlingsområder
              </h3>
              <Link
                to="/behandlinger"
                className="text-xs font-medium text-[#5C7A4F] hover:text-[#382333] underline underline-offset-4"
              >
                Se alle behandlinger
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedTreatments.map((item) => {
                const itemSlug = typeof item.slug === 'object' ? item.slug?._ : item.slug
                const thumb = Array.isArray(item.hero_image) ? item.hero_image[0] : item.hero_image

                return (
                  <Link
                    key={itemSlug}
                    to={`/behandlinger/${itemSlug}`}
                    className="group rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] p-5 shadow-xs transition hover:shadow-md hover:border-[#D5C1D2]"
                  >
                    {thumb && (
                      <div className="aspect-16/10 w-full overflow-hidden bg-[#DFE6D5]">
                        <img
                          src={getAssetUrl(thumb, { width: 400, quality: 80 })}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = thumb
                          }}
                        />
                      </div>
                    )}
                    <span className="text-[10px] font-medium uppercase tracking-wider text-[#8A7283]">
                      {item.category_name}
                    </span>
                    <h4 className="font-serif text-base font-medium text-[#242e1f] group-hover:text-[#5c7a4f] transition-colors mt-0.5">
                      {item.title}
                    </h4>
                    <p className="text-xs text-[#634E5E] line-clamp-2 mt-1">
                      {item.short_description}
                    </p>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Reusable Booking Banner */}
        <BookingBanner />
      </div>
    </>
  )
}
